import React, { useEffect, useState } from "react";
import { Box, Card, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import consolidationService from "../../services/consolidationService";
import { selectActiveTin } from "../../redux/slices/authSlice";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import StatusChip from "../../components/common/StatusChip";
import ConsolidationDetailDialog from "../../components/common/ConsolidationDetailDialog";

const CustomerConsolidations = () => {
    const { enqueueSnackbar } = useSnackbar();
    const activeTin = useSelector(selectActiveTin);
    const [consolidations, setConsolidations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await consolidationService.getAll({ tin: activeTin || undefined });
            setConsolidations(data.data.consolidations || []);
        } catch {
            enqueueSnackbar("Failed to load consolidations", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTin]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Consolidations"
                subtitle="Items you've asked to have sailed and delivered together"
            />

            <Card>
                {loading ? (
                    <SkeletonLoader rows={5} />
                ) : consolidations.length === 0 ? (
                    <EmptyState
                        icon="📦"
                        title="No consolidations yet"
                        description={'Use "Consolidate" on a shipment, or "Add to Consolidation" on an item, to have them shipped together.'}
                    />
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                        <Table sx={{ minWidth: 600 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Consolidation #</TableCell>
                                    <TableCell>Items</TableCell>
                                    <TableCell>Shipments</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Requested</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {consolidations.map((c) => {
                                    const shipmentCount = new Set(
                                        c.items.map((it) => it.shipment?._id || it.shipment),
                                    ).size;
                                    return (
                                        <TableRow key={c._id} hover sx={{ cursor: "pointer" }} onClick={() => setSelectedId(c._id)}>
                                            <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{c.consolidationNumber}</TableCell>
                                            <TableCell>{c.items.length}</TableCell>
                                            <TableCell>{shipmentCount}</TableCell>
                                            <TableCell>
                                                <StatusChip status={c.status} domain="consolidation" />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                                {dayjs(c.createdAt).format("MMM D, YYYY")}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Card>

            <ConsolidationDetailDialog
                open={!!selectedId}
                onClose={() => setSelectedId(null)}
                consolidationId={selectedId}
                isAdmin={false}
                onChanged={load}
            />
        </Box>
    );
};

export default CustomerConsolidations;
